import React, { useState, useEffect } from 'react';
import { Plus, Save, RotateCcw, AlertTriangle, Check, Edit, X } from 'lucide-react';
import { evaluationCriteriaAPI } from '../../services/api';

const EvaluationCriteria = () => {
  const [criteria, setCriteria] = useState({
    fundamental: [],
    persistence: [],
    adaptation: [],
    bonus: []
  });
  
  const [categoryLabels] = useState({
    fundamental: 'CRITÈRES FONDAMENTAUX',
    persistence: 'CRITÈRES DE PERSISTANCE',
    adaptation: 'CRITÈRES D\'ADAPTATION',
    bonus: 'BONUS EXCELLENCE'
  });

  const [validation, setValidation] = useState({
    valid: true,
    main_total: 0,
    bonus_total: 0,
    totals: { fundamental: 0, persistence: 0, adaptation: 0, bonus: 0 }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingCriterion, setEditingCriterion] = useState(null);
  const [editingData, setEditingData] = useState({ description: '', percentage: '' });

  // Charger les critères existants
  useEffect(() => {
    fetchCriteria();
  }, []);

  const fetchCriteria = async () => {
    try {
      const response = await evaluationCriteriaAPI.getAll();
      setCriteria(response.data.data.criteria);
      calculateValidation(response.data.data.criteria);
    } catch (error) {
      console.error('Erreur lors du chargement des critères:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des critères' });
    } finally {
      setLoading(false);
    }
  };

  const calculateValidation = (currentCriteria) => {
    const totals = {
      fundamental: 0,
      persistence: 0,
      adaptation: 0,
      bonus: 0
    };

    Object.keys(currentCriteria).forEach(category => {
      currentCriteria[category].forEach(criterion => {
        if (criterion.is_active) {
          totals[category] += parseFloat(criterion.percentage || 0);
        }
      });
    });

    const mainTotal = totals.fundamental + totals.persistence + totals.adaptation;
    const valid = mainTotal <= 100 && totals.bonus <= 15;

    setValidation({
      valid,
      main_total: mainTotal,
      bonus_total: totals.bonus,
      totals
    });
  };

  const addCriterion = (category) => {
    const newCriterion = {
      id: null,
      name: '',
      description: '',
      percentage: 0,
      category,
      order: criteria[category].length + 1,
      is_active: true
    };

    const updatedCriteria = {
      ...criteria,
      [category]: [...criteria[category], newCriterion]
    };

    setCriteria(updatedCriteria);
    calculateValidation(updatedCriteria);
  };

  const updateCriterion = (category, index, field, value) => {
    const updatedCriteria = { ...criteria };
    updatedCriteria[category][index][field] = value;
    
    setCriteria(updatedCriteria);
    calculateValidation(updatedCriteria);
  };

  const removeCriterion = (category, index) => {
    const updatedCriteria = {
      ...criteria,
      [category]: criteria[category].filter((_, i) => i !== index)
    };
    
    setCriteria(updatedCriteria);
    calculateValidation(updatedCriteria);
  };

  const saveCriteria = async () => {
    if (!validation.valid) {
      setMessage({ 
        type: 'error', 
        text: 'Les pourcentages dépassent les limites autorisées' 
      });
      return;
    }

    setSaving(true);
    
    try {
      const allCriteria = [];
      Object.keys(criteria).forEach(category => {
        criteria[category].forEach(criterion => {
          if (criterion.name && criterion.description) {
            allCriteria.push({
              ...criterion,
              percentage: parseFloat(criterion.percentage || 0)
            });
          }
        });
      });

      const response = await evaluationCriteriaAPI.bulkUpdate({ criteria: allCriteria });
      setMessage({ type: 'success', text: 'Critères sauvegardés avec succès' });
      fetchCriteria();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser aux critères par défaut ?')) {
      return;
    }

    setSaving(true);
    
    try {
      await evaluationCriteriaAPI.resetToDefault();
      setMessage({ type: 'success', text: 'Critères réinitialisés avec succès' });
      fetchCriteria();
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la réinitialisation' });
    } finally {
      setSaving(false);
    }
  };

  const startEditCriterion = (category, index) => {
    const criterion = criteria[category][index];
    setEditingCriterion(`${category}-${index}`);
    setEditingData({
      description: criterion.description || '',
      percentage: criterion.percentage || ''
    });
  };

  const cancelEditCriterion = () => {
    setEditingCriterion(null);
    setEditingData({ description: '', percentage: '' });
  };

  const saveEditCriterion = async (category, index) => {
    const criterion = criteria[category][index];
    
    if (!editingData.description.trim()) {
      setMessage({ type: 'error', text: 'La description ne peut pas être vide' });
      return;
    }

    try {
      if (criterion.id) {
        // Mise à jour d'un critère existant
        await evaluationCriteriaAPI.update(criterion.id, {
          name: criterion.name,
          description: editingData.description.trim(),
          percentage: parseFloat(editingData.percentage || 0),
          category: criterion.category,
          order: criterion.order,
          is_active: criterion.is_active
        });
      } else {
        // Création d'un nouveau critère
        await evaluationCriteriaAPI.create({
          name: editingData.description.trim().substring(0, 50), // Nom basé sur la description
          description: editingData.description.trim(),
          percentage: parseFloat(editingData.percentage || 0),
          category: category,
          order: criteria[category].length,
          is_active: true
        });
      }
      
      setMessage({ type: 'success', text: 'Critère sauvegardé avec succès' });
      setEditingCriterion(null);
      setEditingData({ description: '', percentage: '' });
      fetchCriteria();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du critère:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde du critère' });
    }
  };

  const renderCriterionRow = (criterion, category, index) => {
    const isEditing = editingCriterion === `${category}-${index}`;
    const isNewCriterion = !criterion.id;
    
    return (
      <div key={`${category}-${index}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        <div className="flex-1">
          {isEditing || isNewCriterion ? (
            <input
              type="text"
              placeholder="Description du critère"
              value={isEditing ? editingData.description : criterion.description}
              onChange={(e) => {
                if (isEditing) {
                  setEditingData({ ...editingData, description: e.target.value });
                } else {
                  updateCriterion(category, index, 'description', e.target.value);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <div className="px-3 py-2 text-gray-800">
              {criterion.description || 'Description non définie'}
            </div>
          )}
        </div>
        <div className="w-24">
          {isEditing || isNewCriterion ? (
            <input
              type="number"
              placeholder="%"
              min="0"
              max={category === 'bonus' ? "15" : "100"}
              step="0.1"
              value={isEditing ? editingData.percentage : criterion.percentage}
              onChange={(e) => {
                if (isEditing) {
                  setEditingData({ ...editingData, percentage: e.target.value });
                } else {
                  updateCriterion(category, index, 'percentage', e.target.value);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
            />
          ) : (
            <div className="px-3 py-2 text-center font-medium text-blue-600">
              {criterion.percentage}%
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => saveEditCriterion(category, index)}
                className="px-3 py-2 text-green-600 hover:bg-green-100 rounded-md transition-colors"
                title="Sauvegarder"
              >
                <Save size={16} />
              </button>
              <button
                onClick={cancelEditCriterion}
                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Annuler"
              >
                <X size={16} />
              </button>
            </>
          ) : isNewCriterion ? (
            <button
              onClick={() => removeCriterion(category, index)}
              className="px-3 py-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
              title="Supprimer"
            >
              ×
            </button>
          ) : (
            <>
              <button
                onClick={() => startEditCriterion(category, index)}
                className="px-3 py-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                title="Modifier"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => removeCriterion(category, index)}
                className="px-3 py-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                title="Supprimer"
              >
                ×
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCategorySection = (category) => (
    <div key={category} className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {categoryLabels[category]}
        </h3>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${
            category === 'bonus' 
              ? validation.totals[category] <= 15 ? 'text-green-600' : 'text-red-600'
              : 'text-gray-600'
          }`}>
            Total: {validation.totals[category].toFixed(1)}%
            {category === 'bonus' && ' / 15%'}
          </span>
          <button
            onClick={() => addCriterion(category)}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            Ajouter
          </button>
        </div>
      </div>
      
      <div className="space-y-3">
        {criteria[category].map((criterion, index) => 
          renderCriterionRow(criterion, category, index)
        )}
        
        {criteria[category].length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucun critère défini. Cliquez sur "Ajouter" pour commencer.
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement des critères...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestion des Critères d'Évaluation
          </h1>
          <p className="text-gray-600">
            Configurez les critères d'évaluation des appels commerciaux
          </p>
        </div>

        {/* Validation Summary */}
        <div className={`mb-6 p-4 rounded-lg border ${
          validation.valid 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {validation.valid ? (
              <Check className="text-green-600" size={20} />
            ) : (
              <AlertTriangle className="text-red-600" size={20} />
            )}
            <span className={`font-medium ${
              validation.valid ? 'text-green-800' : 'text-red-800'
            }`}>
              {validation.valid ? 'Configuration valide' : 'Configuration invalide'}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total principal:</span>
              <span className={`ml-2 font-medium ${
                validation.main_total <= 100 ? 'text-green-600' : 'text-red-600'
              }`}>
                {validation.main_total.toFixed(1)}% / 100%
              </span>
            </div>
            <div>
              <span className="text-gray-600">Bonus:</span>
              <span className={`ml-2 font-medium ${
                validation.bonus_total <= 15 ? 'text-green-600' : 'text-red-600'
              }`}>
                {validation.bonus_total.toFixed(1)}% / 15%
              </span>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={saveCriteria}
            disabled={!validation.valid || saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={16} />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          
          <button
            onClick={resetToDefault}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
          >
            <RotateCcw size={16} />
            Réinitialiser
          </button>
        </div>

        {/* Criteria Sections */}
        <div className="space-y-6">
          {['fundamental', 'persistence', 'adaptation', 'bonus'].map(renderCategorySection)}
        </div>
      </div>
    </div>
  );
};

export default EvaluationCriteria;
